export type MessageTransport = 'socket' | 'rest';

/**
 * Socket.IO buffers ordinary emits while disconnected. For durable messages
 * that behaviour is harmful: an old packet can surface much later and the UI
 * has no immediate delivery result. Route disconnected sends through REST,
 * where the existing IndexedDB outbox can handle a transient failure.
 */
export function selectMessageTransport(input: {
  requiresRest: boolean;
  socketConnected: boolean;
}): MessageTransport {
  return input.requiresRest || !input.socketConnected ? 'rest' : 'socket';
}

interface DeliveryErrorLike {
  code?: unknown;
  status?: unknown;
  response?: { status?: unknown };
}

const RETRYABLE_NETWORK_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ECONNRESET',
  'EAI_AGAIN',
]);

/** Return true only when retrying the same clientMessageId later is sensible. */
export function shouldQueueDelivery(error: unknown, browserOnline: boolean): boolean {
  if (!browserOnline) return true;
  if (!error || typeof error !== 'object') return false;

  const candidate = error as DeliveryErrorLike;
  const code = typeof candidate.code === 'string' ? candidate.code.toUpperCase() : '';
  if (RETRYABLE_NETWORK_CODES.has(code)) return true;

  const rawStatus = candidate.response?.status ?? candidate.status;
  const status = typeof rawStatus === 'number' ? rawStatus : Number.NaN;
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
}
